import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Easing,
  KeyboardAvoidingView,
  Modal,
  PanResponder,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native'

import { i18n } from '@localization'
import { publishEvent } from '@zet/analytics'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import {
  BottomSheet,
  H5,
  SH3,
  Stack,
  colors as zetColors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import useMissingFieldsSheetStore, {
  MissingFieldsSheetParams,
} from '@zet/feature-brands/store/missingFieldsSheet'

import { useMissingFields } from '../../api/missingFieldsPopup/hooks'
import { FormBuilder } from './form/form-builder'

const SheetContent = ({ params }: { params: MissingFieldsSheetParams }) => {
  const { data, isLoading } = useMissingFields(
    params?.brandId,
    params?.customerId
  )

  const missingFields = data?.missingFields
  const fieldValues = data?.values

  const ocFields = useMemo(() => {
    const codes = missingFields?.map((item) => item?.code)
    const missedFieldsArr = []
    missingFields?.forEach?.((item) => {
      if (item?.code !== undefined) {
        const matchedField = data?.formFields?.filter(
          (d) => d.code === item.code
        )?.[0]
        if (matchedField) {
          missedFieldsArr.push(matchedField)
        }
      }
    })
    return missedFieldsArr.sort(
      (a, b) => codes?.indexOf(b.code) - codes?.indexOf(a.code)
    )
  }, [missingFields, data?.formFields])

  // The API schema validates EVERY form field, but only the missing ones are
  // rendered — an invalid prefilled value (e.g. a PAN name stored with a
  // trailing space) would keep the whole form permanently invalid with no way
  // to fix it. Scope validation to the fields the user can actually edit.
  const validationSchema = useMemo(() => {
    const fullSchema = data?.validationSchema || {}
    const codes = ocFields?.map((field) => field?.code) || []
    if (!codes.length || !fullSchema?.properties) {
      return fullSchema
    }
    const properties = {}
    codes.forEach((code) => {
      if (fullSchema.properties[code]) {
        properties[code] = fullSchema.properties[code]
      }
    })
    const required = (fullSchema.required || []).filter((code) =>
      codes.includes(code)
    )
    const requiredMessages = fullSchema?.errorMessage?.required || {}
    const scopedRequiredMessages = {}
    required.forEach((code) => {
      if (requiredMessages[code]) {
        scopedRequiredMessages[code] = requiredMessages[code]
      }
    })
    return {
      ...fullSchema,
      required,
      properties,
      ...(fullSchema.errorMessage
        ? {
            errorMessage: {
              ...fullSchema.errorMessage,
              required: scopedRequiredMessages,
            },
          }
        : {}),
    }
  }, [data?.validationSchema, ocFields])

  useEffect(() => {
    publishEvent('screen_view', {
      screen_name:
        Platform.OS === 'web' ? 'MissingFieldsPopup' : 'missing-details',
      brand_name: params?.brandName,
      partner_id: params?.brandId,
      customer_id: params?.customerId,
      customer_name: params?.customerName,
      customer_phone_number: params?.customerPhoneNumber,
    })
  }, [])

  if (isLoading) {
    // Solid height so the dynamically-sized sheet presents (and animates up)
    // right away with a spinner while the partner form loads, instead of an
    // invisible zero-height sheet that pops in when the data arrives.
    return (
      <Stack className="h-[220px] items-center justify-center">
        <ActivityIndicator size={'large'} color={'blue'} />
      </Stack>
    )
  }

  // No missing fields means the data is already complete and the partner is
  // evaluating eligibility in the background — show a checking state (and
  // FormBuilder re-hits generateLink to poll for the outcome) instead of an
  // empty form with a disabled button.
  const isEligibilityCheck = !ocFields?.length

  return (
    <Stack className="pt-3">
      {!isEligibilityCheck && (
        <H5 className="mx-4">
          {i18n.t('missingFieldsPopup.detailsRequired')}
        </H5>
      )}
      <FormBuilder
        isEligibilityCheck={isEligibilityCheck}
        ocFields={ocFields}
        fieldValues={fieldValues}
        validationSchema={validationSchema}
        customerId={params?.customerId}
        generateLinkParams={params?.generateLinkParams}
        brandId={params?.brandId}
        customerName={params?.customerName}
        brandName={params?.brandName}
        customerPhoneNumber={params?.customerPhoneNumber}
        leadSource={params?.leadSource}
        variantId={params?.variantId}
      />
    </Stack>
  )
}

const MissingFieldsBottomSheet = () => {
  const { sheetParams, closeSheet } = useMissingFieldsSheetStore()
  const navigation = useNavigation()

  // Close the sheet whenever navigation state changes underneath it —
  // e.g. the browser back button on web pops the route but this globally
  // mounted sheet would otherwise stay open over the new page.
  useEffect(() => {
    if (!sheetParams) {
      return
    }
    const unsubscribe = navigation?.addListener?.('state', () => {
      closeSheet()
    })
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(sheetParams)])

  // Mount the sheet fresh on every open: a permanently-mounted gorhom modal
  // measures ~zero content height and then jumps to the form height without
  // animating; a fresh mount presents with the normal slide-up.
  if (!sheetParams) {
    return null
  }

  // Native hosts the form in a plain RN Modal: the gorhom sheet's dynamic
  // content sizing collapses (index -1 → force dismiss) when the keyboard
  // opens over a tall form, closing the sheet mid-typing. Web keeps the
  // design-system sheet, which has no such interaction.
  if (Platform.OS !== 'web') {
    return (
      <NativeMissingFieldsSheet params={sheetParams} closeSheet={closeSheet} />
    )
  }

  return (
    <BottomSheet
      visible={Boolean(sheetParams)}
      disableBottomSheet={closeSheet}
      onDismiss={closeSheet}
      sheetName="missing_fields_details_required"
    >
      <SheetContent params={sheetParams} />
    </BottomSheet>
  )
}

// Native flow: keep a full-screen loader (visually continuous with the
// ShareCardLink loader shown during generateLink) until the partner form has
// loaded, THEN slide the sheet up fully formed — no half-open spinner panel
// that suddenly jumps to the form height.
const SCREEN_HEIGHT = Dimensions.get('window').height
const DRAG_DISMISS_DISTANCE = 120
const DRAG_DISMISS_VELOCITY = 0.8

const NativeMissingFieldsSheet = ({
  params,
  closeSheet,
}: {
  params: MissingFieldsSheetParams
  closeSheet: () => void
}) => {
  const { isLoading } = useMissingFields(params?.brandId, params?.customerId)

  // Tracks the panel Modal's first native frame (onShow): the loader overlay
  // stays mounted underneath until then, so no white gap can show between
  // dialog windows.
  const [isPanelModalShown, setIsPanelModalShown] = useState(false)

  // The panel's slide is driven by our own Animated transform inside a
  // single always-mounted Modal — Android skips a new Modal's enter
  // animation when it replaces one being torn down in the same frame. The
  // backdrop starts at the loader's darkness and eases to the sheet dim so
  // the loader → sheet handoff doesn't visibly flash brighter.
  const panelTranslateY = useRef(new Animated.Value(SCREEN_HEIGHT)).current
  const dimOpacity = useRef(new Animated.Value(0.8)).current

  useEffect(() => {
    if (!isLoading) {
      Animated.parallel([
        Animated.timing(panelTranslateY, {
          toValue: 0,
          duration: 300,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(dimOpacity, {
          toValue: 0.5,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const animateClose = () => {
    Animated.parallel([
      Animated.timing(panelTranslateY, {
        toValue: SCREEN_HEIGHT,
        duration: 250,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(dimOpacity, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => closeSheet())
  }

  // Swipe-down on the handle area dismisses the sheet, matching the
  // affordance the handle advertises.
  const dragResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_event, gesture) =>
        gesture.dy > 4 && Math.abs(gesture.dy) > Math.abs(gesture.dx),
      onPanResponderMove: (_event, gesture) => {
        if (gesture.dy > 0) {
          panelTranslateY.setValue(gesture.dy)
        }
      },
      onPanResponderRelease: (_event, gesture) => {
        if (
          gesture.dy > DRAG_DISMISS_DISTANCE ||
          gesture.vy > DRAG_DISMISS_VELOCITY
        ) {
          animateClose()
        } else {
          Animated.spring(panelTranslateY, {
            toValue: 0,
            useNativeDriver: true,
          }).start()
        }
      },
    })
  ).current

  return (
    <>
      {/* Plain View overlay (NOT a Modal): it paints in the same commit that
          tears down ShareCardLink's loader dialog, and it outlives the panel
          Modal's creation until onShow — so neither dialog swap can flash
          the white screen underneath. */}
      {(isLoading || !isPanelModalShown) && (
        <Stack
          className="bg-grays-100 justify-center"
          style={styles.loaderFill}
        >
          <ActivityIndicator size={'large'} color={'white'} />
          <SH3 className="mx-3 mt-6 text-center text-white">
            {i18n.t('shareCardLink.loaderText')}
          </SH3>
        </Stack>
      )}
      {/* No statusBarTranslucent on the Modal: on Android a transparent
          Modal with it paints an opaque white first frame during the
          edge-to-edge re-layout (RN issue) — the exact flash this sheet
          had. */}
      {!isLoading && (
        <Modal
          visible
          transparent
          animationType="none"
          onShow={() => setIsPanelModalShown(true)}
          onRequestClose={animateClose}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={styles.flexOne}
          >
            <Stack style={styles.modalContainer}>
              <Animated.View
                pointerEvents="none"
                style={[styles.dim, { opacity: dimOpacity }]}
              />
              <Pressable style={styles.backdrop} onPress={animateClose} />
              <Animated.View
                style={[
                  styles.panel,
                  { transform: [{ translateY: panelTranslateY }] },
                ]}
              >
                <Stack
                  {...dragResponder.panHandlers}
                  style={styles.handleArea}
                >
                  <Stack style={styles.handle} />
                </Stack>
                <ScrollView keyboardShouldPersistTaps="handled">
                  <SheetContent params={params} />
                </ScrollView>
              </Animated.View>
            </Stack>
          </KeyboardAvoidingView>
        </Modal>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  loaderFill: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.8,
    zIndex: 999,
    elevation: 10,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  dim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: zetColors.grays[100],
  },
  flexOne: {
    flex: 1,
  },
  handle: {
    alignSelf: 'center',
    width: 48,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#D5D9DD',
  },
  handleArea: {
    alignItems: 'center',
    alignSelf: 'stretch',
    paddingBottom: 6,
    paddingTop: 10,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  panel: {
    maxHeight: '90%',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    backgroundColor: '#FFFFFF',
    paddingBottom: 16,
  },
})

export default MissingFieldsBottomSheet
